import useClickOutside from "@/hooks/useClickOutside"
import { UserButton, useUser } from "@clerk/clerk-react"
import { UserResource } from "@clerk/types"
import { ChevronFirst, ChevronLast } from "lucide-react"
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
} from "react"
import { FiChevronDown, FiChevronUp } from "react-icons/fi"
import { RiMenuFold4Line } from "react-icons/ri"
import { useMediaQuery } from "react-responsive"
import { Link } from "react-router-dom"
import { Card, CardContent } from "./Card"
import Logo from "./icons/Logo"

//@ts-ignore
interface SidebarContext {
  expanded: boolean
  setExpanded: Dispatch<SetStateAction<boolean>>
}
const SidebarContext = createContext<SidebarContext | null>(null)

interface SidebarHeaderProps {
  expanded: boolean
  setExpanded: Dispatch<SetStateAction<boolean>>
  mobile?: boolean
}
// Sidebar Header Component
function SidebarHeader({
  expanded,
  setExpanded,
  mobile = false,
}: SidebarHeaderProps) {
  return (
    <div className="pb-4">
      <div className="px-4 py-3 flex justify-center sm:justify-between items-center">
        <div className="flex items-center gap-2 text-white">
          <Logo width="50px" stroke="#001a50" />
          <span
            className={`text-2xl font-bold text-[#001a50] ${!expanded && "hidden"}`}
          >
            AutoLink
          </span>
        </div>
        <button
          onClick={() => setExpanded((curr: any) => !curr)}
          className="p-1.5 rounded-lg bg-gray-50 hover:bg-gray-100"
        >
          {!mobile && (expanded ? <ChevronFirst /> : <ChevronLast />)}
        </button>
      </div>
      <hr />
    </div>
  )
}

// Sidebar Content Component
function SidebarContent({
  children,
  expanded,
  setExpanded,
}: {
  children: ReactNode
  expanded: boolean
  setExpanded: Dispatch<SetStateAction<boolean>>
}) {
  return (
    <SidebarContext.Provider value={{ expanded, setExpanded }}>
      <ul className={`flex-1 px-3 flex flex-col ${expanded ? "" : "gap-0"}`}>
        {children}
      </ul>
    </SidebarContext.Provider>
  )
}

// Sidebar Footer Component
function SidebarFooter({
  expanded,
  user,
}: {
  expanded: boolean
  user: UserResource | null | undefined
}) {
  return (
    <div className="border-t flex p-3">
      <UserButton />
      <div
        className={`flex justify-between items-center sm:overflow-hidden transition-all ${expanded ? "w-52 ml-3" : "w-0"}`}
      >
        <div className="leading-4">
          <h4 className="font-semibold">{user?.username}</h4>
          <span className="text-xs text-gray-600">
            {user?.emailAddresses[0].emailAddress}
          </span>
        </div>
      </div>
    </div>
  )
}

// Desktop Sidebar Component
function SidebarDesktop({ children }: { children: ReactNode }) {
  const [expanded, setExpanded] = useState(false)
  const { user } = useUser()

  return (
    <aside className="h-screen block">
      <nav className="h-full flex flex-col bg-gray-50 border-r shadow-sm">
        <SidebarHeader expanded={expanded} setExpanded={setExpanded} />
        <SidebarContent
          children={children}
          expanded={expanded}
          setExpanded={setExpanded}
        />
        <SidebarFooter expanded={expanded} user={user} />
      </nav>
    </aside>
  )
}

// Mobile Sidebar Component
function SidebarMobile({
  children,
  mobileSidebarOpen,
  setMobileSidebarOpen,
}: {
  children: ReactNode
  mobileSidebarOpen: boolean
  setMobileSidebarOpen: Dispatch<SetStateAction<boolean>>
}) {
  const { user } = useUser()
  // const [expanded, setExpanded] = useState(false)

  return (
    <aside
      className={`fixed top-0 left-0 w-full h-full bg-gray-50 z-50 transform transition-transform ${mobileSidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
    >
      <nav className="h-full flex flex-col border-r shadow-sm">
        <SidebarHeader
          expanded={mobileSidebarOpen}
          setExpanded={setMobileSidebarOpen}
          mobile={true}
        />
        <SidebarContent
          children={children}
          expanded={mobileSidebarOpen}
          setExpanded={setMobileSidebarOpen}
        />
        <SidebarFooter expanded={mobileSidebarOpen} user={user} />
      </nav>
      <button
        onClick={() => setMobileSidebarOpen(false)}
        className="absolute top-4 right-4"
      >
        ✕
      </button>
    </aside>
  )
}

// Composite Sidebar Component
export default function Sidebar({ children }: { children: ReactNode }) {
  const isDesktop = useMediaQuery({ minWidth: 768 })
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)

  return (
    <>
      {isDesktop ? (
        <SidebarDesktop children={children} />
      ) : (
        <>
          <button
            onClick={() => setMobileSidebarOpen(true)}
            className="sm:hidden p-2 absolute top-4 left-2 z-50"
          >
            <RiMenuFold4Line size={25} />
          </button>
          <SidebarMobile
            children={children}
            mobileSidebarOpen={mobileSidebarOpen}
            setMobileSidebarOpen={setMobileSidebarOpen}
          />
        </>
      )}
    </>
  )
}

export function SidebarItem(props: {
  icon: any
  text: string
  path: string
  active?: boolean
  alert?: boolean
}) {
  const sidebarContext = useContext(SidebarContext)
  if (sidebarContext === null) {
    throw new Error("SidebarItes must be used within a SidebarProvider")
  }
  const { expanded, setExpanded } = sidebarContext
  const isDesktop = useMediaQuery({ minWidth: 768 })

  return (
    <li
      className={`relative flex items-center py-2 px-3 my-1 font-medium rounded-md cursor-pointer transition-colors group ${
        props.active
          ? "bg-gradient-to-tr from-indigo-200 to-indigo-100 text-indigo-800"
          : "hover:bg-indigo-50 text-gray-600"
      } ${!expanded ? "justify-center" : ""}`}
      onClick={() => setExpanded(false)}
    >
      <Link to={props.path} className="flex">
        {props.icon}
        <span
          className={`overflow-hidden transition-all ${
            expanded ? "w-52 ml-3" : "size-0"
          }`}
        >
          {props.text}
        </span>
        {props.alert && (
          <div
            className={`absolute right-2 w-2 h-2 rounded bg-indigo-400 ${
              expanded ? "" : "top-2"
            }`}
          />
        )}
        {!expanded && isDesktop && (
          <div
            className={`absolute left-full rounded-md px-2 py-1 ml-6 bg-indigo-100 text-indigo-800 text-sm invisible opacity-20 -translate-x-3 transition-all group-hover:visible group-hover:opacity-100 group-hover:translate-x-0 z-10 w-full`}
          >
            {props.text}
          </div>
        )}
      </Link>
    </li>
  )
}

export function SidebarItemWithSubMenu(props: {
  icon: any
  text: string
  active?: boolean
  alert?: boolean
  subItems?: {
    text: string
    path: string
    icon: React.ComponentType<any> | string
  }[] // Add subItems prop for the dropdown
}) {
  const sidebarContext = useContext(SidebarContext)
  if (sidebarContext === null) {
    throw new Error("SidebarItes must be used within a SidebarProvider")
  }
  const { expanded, setExpanded } = sidebarContext
  const [isOpen, setIsOpen] = useState(false) // Manage the dropdown open/close state
  const isDesktop = useMediaQuery({ minWidth: 768 })

  // Use the click outside hook
  const dropdownRef = useClickOutside<HTMLLIElement>(() => setIsOpen(false))

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev)
  }

  return (
    <li
      className={`relative flex flex-col items-start py-2 px-3 my-1 font-medium rounded-md cursor-pointer transition-colors group text-gray-600 ${
        props.active &&
        !isOpen &&
        "bg-gradient-to-tr from-indigo-200 to-indigo-100 text-indigo-800"
      }`}
      onClick={(e) => {
        e.stopPropagation() // Prevent click from closing the parent dropdown
        if (props.subItems) {
          toggleDropdown() // Toggle the sub-item visibility if there are sub-items
        } else {
          setExpanded(false)
        }
      }}
      ref={dropdownRef}
    >
      <div
        className={`flex items-center w-full ${
          !expanded ? "justify-center" : ""
        }`}
      >
        {props.icon}
        <span
          className={`overflow-hidden transition-all flex justify-between  ${
            expanded ? "w-full ml-3" : "w-0 overflow-auto"
          }`}
        >
          {props.text}
          {isOpen ? <FiChevronUp size={20} /> : <FiChevronDown size={20} />}
        </span>
      </div>
      {props.alert && (
        <div
          className={`absolute right-2 w-2 h-2 rounded bg-indigo-400 ${
            expanded ? "" : "top-2"
          }`}
        />
      )}

      {/* Dropdown sub-items */}

      {props.subItems && isOpen && (
        <div className="w-full">
          {/* Conditionally render the Card if not expanded */}
          {/* Render another list of subItems */}

          {!expanded ? (
            <div className="relative">
              <Card className="absolute left-[6.5rem] min-w-64 z-10">
                <CardContent>
                  <ul className="mt-2 space-y-2 flex justify-start flex-col left-28 pl-0">
                    {props.subItems.map((subItem) => (
                      //@TODO
                      //Create active flag for subItems
                      <li
                        key={subItem.path}
                        className={`flex items-center py-1 px-2 text-gray-600 hover:bg-indigo-100 rounded-md gap-4 ${
                          window.location.href.toString().includes(subItem.path)
                            ? "bg-gradient-to-tr from-indigo-200 to-indigo-100 text-indigo-800"
                            : "hover:bg-indigo-50 text-gray-600"
                        }`}
                        onClick={() => {
                          setExpanded(false)
                        }}
                      >
                        <Link
                          to={subItem.path}
                          className="text-lg flex items-center gap-2"
                        >
                          {typeof subItem.icon === "string" ? (
                            <span className="px-1 text-2xl">
                              {subItem.icon}
                            </span>
                          ) : (
                            <subItem.icon size={30} />
                          )}
                          {subItem.text}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          ) : (
            <ul className="pl-6 mt-3 space-y-2 flex justify-start flex-col">
              {props.subItems.map((subItem) => (
                <li
                  key={subItem.path}
                  className={`py-1 px-2 text-gray-600 hover:bg-indigo-100 rounded-md gap-4 w-full ${
                    window.location.href.toString().includes(subItem.path)
                      ? "bg-gradient-to-tr from-indigo-200 to-indigo-100 text-indigo-800"
                      : "hover:bg-indigo-50 text-gray-600"
                  }`}
                  onClick={() => {
                    setExpanded(false)
                  }}
                >
                  <Link to={subItem.path} className="flex items-center gap-2">
                    {typeof subItem.icon === "string" ? (
                      <span className="text-2xl">{subItem.icon}</span>
                    ) : (
                      <subItem.icon size={30} />
                    )}
                    {subItem.text}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Tooltip for desktop */}
      {!expanded && isDesktop && (
        <div
          className={`absolute left-full rounded-md px-2 py-1 ml-6 bg-indigo-100 text-indigo-800 text-sm invisible opacity-20 -translate-x-3 transition-all group-hover:visible group-hover:opacity-100 group-hover:translate-x-0 z-10 w-full`}
        >
          {props.text}
        </div>
      )}
    </li>
  )
}

export function SidebarTitle(props: { titleTex: string; styles?: string }) {
  const sidebarContext = useContext(SidebarContext)
  if (sidebarContext === null) {
    throw new Error("SidebarItes must be used within a SidebarProvider")
  }
  const { expanded } = sidebarContext
  return expanded ? (
    <div className={`text-xl py-4 ${props.styles}`}>
      <h3>{props.titleTex}</h3>
    </div>
  ) : (
    <></>
  )
}
